import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { faList } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, Grid, useMediaQuery } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { Enrollment, Membership } from '@spike/membership-model';
import clsx from 'clsx';
import { ToolbarButton, ToolbarItem } from 'components/UI';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import SummaryCard from 'components/UI/SummaryCard';
import { useApiClientWrapper } from 'hooks';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SellMembership from '../Sell/SellMembership';
import EnrollmentDetails from '../Enrollment/EnrollmentDetails';
import MembershipDetailsModal from './MembershipDetailsModal';
import {
    fetchMembershipEnrollmentsThunk,
    MembershipsState,
    MembershipsStatus,
    retryPaymentThunk,
    searchEnrollmentsThunk,
    getEnrollmentThunk,
    retryMultiplePaymentsThunk
} from '@spike/memberships-action';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import UpdateEnrollmentCardDrawer from '../Enrollment/UpdateEnrollmentCardDrawer';
import useMembershipDetailsStyles from './MembershipDetailsStyles';
import EnrollmentListView from './EnrollmentListView';
import { useEnrollmentFilters } from 'components/Memberships/SoldMemberships/hooks/useEnrollmentFilters';
import type { TableColumn } from 'components/Memberships/Detail/MembershipDetailsTable';
import { useDebounce } from 'hooks/useDebounce';

const columnsForSoldMemberships: TableColumn[] = [
    'client',
    'pet',
    'startDate',
    'endDate',
    'nextBilling',
    'status',
    'total',
    'actions'
];

interface MembershipDetailsProps {
    membership: Membership;
    refreshMemberships: () => void;
    onClose: () => void;
}

const MembershipDetails: FunctionComponent<MembershipDetailsProps> = ({ membership, refreshMemberships, onClose }) => {
    const classes = useMembershipDetailsStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 300);
    const [showSearch, setShowSearch] = useState(false);
    const [showMobileDropdown, setShowMobileDropdown] = useState(false);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const enrollments = useSelector<RootState, Enrollment[]>(state => state.memberships.enrollments);
    const enrollmentsPagination = useSelector<RootState, MembershipsState['enrollmentsPagination'] | null>(
        state => state.memberships.enrollmentsPagination
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedOption, setSelectedOption] = useState('active');
    const [pageSize, setPageSize] = useState(10);

    const [showMembershipDetailsModal, setShowMembershipDetailsModal] = useState(false);
    const [showSellMembership, setShowSellMembership] = useState(false);
    const [showEnrollmentDetails, setShowEnrollmentDetails] = useState(false);
    const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const [lastFetchedStatus, setLastFetchedStatus] = useState(selectedOption);
    const [showClientCards, setShowClientCards] = useState(false);
    const enrollmentState = useSelector<RootState, Enrollment | null>(state => state.memberships.enrollmentSelected);
    const [loadingCards, setLoadingCards] = useState(false);
    const [localEnrollment, setLocalEnrollment] = useState<Enrollment | null>(null);

    useHideHubSpotChat(showMobileDropdown);

    const summaryCardData = useMemo(
        () => [
            { title: 'Subscribed', value: membership.subscribedCount },
            { title: 'Canceled', value: membership.canceledCount },
            //{ title: 'Paused', value: membership.pausedCount },
            { title: 'Total Sales', value: `$${Number(membership.totalSales).toFixed(2)}` }
        ],
        [membership]
    );

    const getEnrollments = () => {
        if (membership.id) {
            setLastFetchedStatus(selectedOption);
            if (debouncedSearchText.length >= 3) {
                dispatch(
                    searchEnrollmentsThunk(
                        apiClientWrapper,
                        debouncedSearchText,
                        membership.id,
                        currentPage,
                        pageSize,
                        selectedOption === 'all' ? undefined : selectedOption
                    )
                );
            } else if (debouncedSearchText.length === 0) {
                dispatch(
                    fetchMembershipEnrollmentsThunk(
                        apiClientWrapper,
                        membership.id,
                        currentPage,
                        selectedOption === 'all' ? undefined : selectedOption,
                        pageSize
                    )
                );
            }
        }
    };

    useEffect(() => {
        setLoadingCards(false);
    }, [localEnrollment]);

    useEffect(() => {
        getEnrollments();
    }, [membership.id, currentPage, selectedOption, pageSize, debouncedSearchText]);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.GetEnrollmentsSuccess:
                setLoading(false);
                break;
            case MembershipsStatus.GettingEnrollments:
                setLoading(true);
                break;
            case MembershipsStatus.GetEnrollmentsError:
                setLoading(false);
                break;
            case MembershipsStatus.CancelEnrollmentSuccess:
                getEnrollments();
                refreshMemberships();
                break;
            case MembershipsStatus.RetryPaymentSuccess:
                setLoading(true);
                refreshEnrollments();
                break;
            case MembershipsStatus.SearchingEnrollments:
                setLoading(true);
                break;
            case MembershipsStatus.SearchEnrollmentsSuccess:
                setLoading(false);
                break;
            case MembershipsStatus.SearchEnrollmentsError:
                setLoading(false);
                break;
            case MembershipsStatus.GetEnrollmentSuccess:
                setLocalEnrollment(enrollmentState);
                break;
            case MembershipsStatus.GetEnrollmentError:
                setLoadingCards(false);
                break;
            case MembershipsStatus.GettingEnrollment:
                setLoadingCards(true);
                break;
            case MembershipsStatus.RetryMultiplePaymentsSuccess:
                setLoading(true);
                refreshEnrollments();
                break;
        }
    }, [membershipsStatus]);

    const getEnrollment = (enrollment: Enrollment) => {
        setLoadingCards(true);
        dispatch(getEnrollmentThunk(apiClientWrapper, enrollment.id));
    };

    const handleStatusFilter = (option: string) => {
        setSelectedOption(option);
        setCurrentPage(1);
        setLastFetchedStatus(option);
    };

    const handleSearchTextChange = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const handleClickSearch = () => {
        setShowSearch(!showSearch);
        setSearchText('');
        setCurrentPage(1);
    };

    const buttonLabel = () => {
        return isMobile ? 'Sell' : 'Sell Membership';
    };

    const handleEnrollmentClick = (enrollment: Enrollment) => {
        setSelectedEnrollmentId(enrollment.id);
        setShowEnrollmentDetails(true);
    };

    const handleUpdateEnrollment = (enrollment: Enrollment) => {
        setLoadingCards(true);
        getEnrollment(enrollment);
        setShowClientCards(true);
    };

    const handleRetryPayment = (enrollment: Enrollment) => {
        let enrollmentIds = enrollment.pastDueEnrollments
            ? enrollment.pastDueEnrollments.map(e => e.id).join(',')
            : enrollment.id.toString();
        if (enrollment.pastDueEnrollments && enrollment.pastDueEnrollments.length > 0) {
            enrollmentIds = enrollmentIds + ',' + enrollment.id;
        }
        //dispatch(retryPaymentThunk(apiClientWrapper, enrollment.id));
        dispatch(retryMultiplePaymentsThunk(apiClientWrapper, enrollmentIds));
    };

    const handleCloseEnrollmentDetails = () => {
        setShowEnrollmentDetails(false);
    };

    const handleCloseClientCards = () => {
        setShowClientCards(false);
    };

    const handleSellMembership = () => setShowSellMembership(true);
    const handleCloseSellMembership = () => setShowSellMembership(false);

    const refreshEnrollments = () => {
        setTimeout(() => {
            getEnrollments();
            refreshMemberships();
        }, 3000);
    };

    const onPaid = () => {
        setLoading(true);
        refreshEnrollments();
        /*
        setTimeout(() => {
            refreshMemberships();
            getEnrollments();
        }, 3000);
        */
        //setShowSellMembership(false);
    };

    const sellMembership = (
        <SellMembership
            onClose={handleCloseSellMembership}
            onPaid={onPaid}
        />
    );

    const enrollmentDetails = () =>
        selectedEnrollmentId && (
            <EnrollmentDetails
                enrollmentId={selectedEnrollmentId}
                onClose={handleCloseEnrollmentDetails}
            />
        );

    const onFinishUpdate = (enrollment: Enrollment | null) => {
        if (!enrollment) {
            return;
        }

        if (enrollment.status != 'past_due') {
            getEnrollment(enrollment);
        }
    };

    const clientCards = () =>
        showClientCards && (
            <UpdateEnrollmentCardDrawer
                onClose={handleCloseClientCards}
                enrollment={localEnrollment}
                loading={loadingCards}
                onFinishUpdate={() => onFinishUpdate(localEnrollment)}
            />
        );

    const membershipTitle = () => {
        return (
            <div className={classes.membershipTitle}>
                {membership.name}{' '}
                <Chip
                    className={clsx(
                        classes.customBadge,
                        membership.active ? classes.badgeActive : classes.badgeInactive
                    )}
                    label={membership.active ? 'Active' : 'Inactive'}
                />
            </div>
        );
    };

    const showMembershipDetails = () => {
        setShowMembershipDetailsModal(true);
    };

    const titleTooltipOptions = () => {
        return isMobile ? (
            <button
                type="button"
                aria-label="Membership Details"
                className={classes.membershipOptionsButton}
                onClick={() => setShowMobileDropdown(true)}
            >
                <FontAwesomeIcon icon={faEllipsis} />
            </button>
        ) : (
            <ToolbarButton
                id="memberships_toolbar"
                className={classes.toolbarButton}
            >
                <ToolbarItem
                    text={'Membership Details'}
                    icon={faList}
                    onClick={showMembershipDetails}
                />
            </ToolbarButton>
        );
    };

    const { getFilterOptions, getAlerts } = useEnrollmentFilters({
        loading,
        lastFetchedStatus,
        enrollmentsPagination
    });

    const alerts = getAlerts(classes);

    const tableProps = {
        loading,
        enrollments,
        pagination: enrollmentsPagination,
        onPageChange: setCurrentPage,
        pageSize,
        onPageSizeChange: setPageSize,
        filterSelectedOption: selectedOption,
        onEnrollmentClick: handleEnrollmentClick,
        onUpdateEnrollment: handleUpdateEnrollment,
        onRetryPayment: handleRetryPayment,
        onPaid: onPaid,
        columns: columnsForSoldMemberships
    };

    const membershipDetailsContent = () => {
        return (
            <Box className={classes.wrapperContainer}>
                <EnrollmentListView
                    title={membershipTitle()}
                    showSellButton={membership.active}
                    onSellClick={handleSellMembership}
                    buttonLabel={buttonLabel()}
                    onBack={onClose}
                    tooltipOptions={titleTooltipOptions()}
                    filters={{
                        options: getFilterOptions(),
                        selectedId: selectedOption,
                        onSelect: handleStatusFilter,
                        className: classes.SelectionBar
                    }}
                    search={{
                        value: searchText,
                        setValue: handleSearchTextChange,
                        className: classes.searchBarContainer,
                        placeholder: 'Search by client or pet'
                    }}
                    tableProps={tableProps}
                    classes={{
                        wrapperContainer: classes.wrapperContainer,
                        headWrapper: classes.headWrapper,
                        alertsContainer: classes.alertsContainer,
                        content: classes.content,
                        headerContainer: classes.headerContainer
                    }}
                    alerts={alerts}
                    headerContent={
                        <Box className={classes.contentSummaryCard}>
                            {summaryCardData.map((card, index) => (
                                <Grid
                                    key={index}
                                    item
                                    xs={12}
                                    md={4}
                                    className={classes.cardSummaryContainer}
                                >
                                    <SummaryCard
                                        key={index}
                                        title={card.title}
                                        value={card.value ?? 0}
                                        color={'white'}
                                        className={classes.cardSummary}
                                    />
                                </Grid>
                            ))}
                        </Box>
                    }
                />

                {showMembershipDetailsModal && (
                    <MembershipDetailsModal
                        open={showMembershipDetailsModal}
                        onClose={() => setShowMembershipDetailsModal(false)}
                        membership={membership}
                    />
                )}

                {showMobileDropdown && isMobile && (
                    <ActionsPopup
                        actions={[
                            {
                                label: 'Membership Details',
                                icon: faList,
                                onClick: () => {
                                    setShowMobileDropdown(false);
                                    showMembershipDetails();
                                }
                            }
                        ]}
                        onClose={() => setShowMobileDropdown(false)}
                    />
                )}
            </Box>
        );
    };

    return (
        <>
            {!showEnrollmentDetails && membershipDetailsContent()}
            {showEnrollmentDetails && enrollmentDetails()}
            {showClientCards && clientCards()}
            {showSellMembership && sellMembership}
        </>
    );
};

export default MembershipDetails;
